import React, { useState } from "react";
import { api, formatApiErrorDetail, euro } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Replace } from "lucide-react";
import { useOperator } from "@/components/mobile/operators";
import { UnoNumberCheck, useUnoCheck } from "@/components/mobile/UnoNumberCheck";
import { UnoMobileDealerBox } from "@/components/mobile/UnoMobileBox";
import { SimTypePick } from "@/components/mobile/UnoActivationOptions";
import { FileField, toB64 } from "@/components/mobile/FileField";

const EMPTY = { phone_number: "", sim_type: "sim", sim_iccid: "", email: "", swap_reason: "1", note: "" };
const ok = (msg) => { toast.error(msg); return false; };

export const SimSwapTab = ({ terms, simStock }) => {
  const op = useOperator();
  const uno = op.key === "unomobile";
  const live = uno && !!terms.portal_on;
  const [form, setForm] = useState(EMPTY);
  const [docFile, setDocFile] = useState(null);
  const [modFile, setModFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const check = useUnoCheck(live, form.phone_number);
  const esim = uno && form.sim_type === "esim";
  const fees = terms.service_fees || {};
  const fee = Number((esim ? fees.sostituzione_esim : fees.sostituzione_sim) || 0);
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const reset = () => { setForm(EMPTY); setDocFile(null); setModFile(null); setCreated(null); };
  const valid = () => {
    if (form.phone_number.replace(/\D/g, "").length < 9) return ok(`Inserisci il numero ${op.name} del cliente`);
    if (live && (!check || check.loading)) return ok("Attendi il controllo del numero sul portale");
    if (check?.found === false) return ok("Il numero non è di questo operatore");
    if (!esim && form.sim_iccid.length < 18) return ok("Inserisci l'ICCID della nuova SIM");
    if (esim && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) return ok("Per l'eSIM serve l'email del cliente");
    if (!docFile) return ok("Carica il documento d'identità del cliente");
    return true;
  };
  const submit = async () => {
    if (!valid()) return;
    setBusy(true);
    try {
      const payload = { request_type: "sostituzione_sim", operator: op.key, ...form, sim_type: esim ? "esim" : "sim", doc_base64: await toB64(docFile), doc_filename: docFile.name, ...(modFile ? { module_base64: await toB64(modFile), module_filename: modFile.name } : {}) };
      const { data } = await api.post("/italiamobile/requests", payload);
      window.dispatchEvent(new Event("sim-changed"));
      if (live) { setCreated({ id: data.id, operator: op.key, unomobile: { stage: "in_invio", busy: true } }); toast.success("Sostituzione avviata sul portale Uno Mobile"); }
      else { toast.success(`Richiesta di sostituzione inviata all'amministrazione (ID ${data.id.slice(-6)})`); reset(); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Richiesta non inviata"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-4 sm:p-5 glow-border space-y-4" data-testid="im-swap-card">
      <p className="text-sm text-muted-foreground" data-testid="im-swap-description">{live ? "Sostituisci la SIM di un cliente: la nuova SIM prende lo stesso numero e il credito. Parte da sola sul portale Uno Mobile; se serve, il cliente riceve un SMS con un codice da scrivere qui." : `La richiesta arriva all'amministrazione, che la lavora con ${op.name}.`}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Numero {op.name} *</Label><Input type="tel" className="mt-1.5" data-testid="im-swap-phone" value={form.phone_number} onChange={set("phone_number")} placeholder="+39 3xx…" /><div className="mt-1.5"><UnoNumberCheck res={check} testid="im-swap-check" /></div></div>
        <div><Label>Motivo</Label>
          <Select value={form.swap_reason} onValueChange={(v) => setForm((s) => ({ ...s, swap_reason: v }))}>
            <SelectTrigger className="mt-1.5" data-testid="im-swap-reason"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(terms.swap_reasons || {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {uno && <SimTypePick value={form.sim_type} onChange={(v) => setForm((s) => ({ ...s, sim_type: v }))} testid="im-swap-type" />}
      {!esim && simStock === 0 && <p className="text-sm rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-amber-700 dark:text-amber-300" data-testid="im-swap-no-sim">Non hai SIM {op.name} in magazzino: ordinane una prima di sostituire{uno ? ", oppure scegli eSIM" : ""}.</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        {esim
          ? <div><Label>Email del cliente *</Label><Input type="email" className="mt-1.5" data-testid="im-swap-email" value={form.email} onChange={set("email")} placeholder="Qui arriva il QR dell'eSIM" /></div>
          : <div><Label>ICCID della nuova SIM *</Label><Input inputMode="numeric" className="mt-1.5 font-mono" data-testid="im-swap-iccid" value={form.sim_iccid} onChange={(e) => setForm((s) => ({ ...s, sim_iccid: e.target.value.replace(/\D/g, "").slice(0, 20) }))} placeholder="19 cifre" /><p className="text-xs text-muted-foreground mt-1">Usi una SIM {op.name} del tuo magazzino.</p></div>}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <FileField label="Documento d'identità del cliente *" testid="im-swap-doc" file={docFile} onPick={setDocFile} onClear={() => setDocFile(null)} />
        <FileField label="Modulo firmato o denuncia (se SIM rubata)" testid="im-swap-module" file={modFile} onPick={setModFile} onClear={() => setModFile(null)} />
      </div>
      <div><Label>Note</Label><Textarea rows={1} className="mt-1 min-h-0" data-testid="im-swap-note" value={form.note} onChange={set("note")} /></div>
      <p className="text-sm" data-testid="im-swap-cost">{fee > 0 ? <>Dal tuo Plafond scaliamo <strong>{euro(fee)}</strong> <span className="text-muted-foreground">(prezzo pubblico {op.name}{esim ? " per l'eSIM" : ""}). Se non va a buon fine te lo rimborsiamo.</span></> : <span className="text-muted-foreground">Nessun costo dal tuo Plafond.</span>}</p>
      {created ? <div className="space-y-2">
        <UnoMobileDealerBox request={created} onChanged={setCreated} />
        <Button variant="ghost" className="w-full" onClick={reset} data-testid="im-swap-new">+ Nuova sostituzione</Button>
      </div> : <Button onClick={submit} disabled={busy} className="gap-2" data-testid="im-swap-submit"><Replace className="w-4 h-4" />{busy ? "Invio…" : "Sostituisci la SIM"}</Button>}
    </Card>
  );
};
